import {ProgramPaths} from "@/enum/ProgramPaths";

/**
 * Otomatik (zamanlanmış) sorgu kapısı — istemci tarafı (DEV-2603, karar 20.10.2026).
 *
 * UYAP yoğun istek yüzünden kısıtlama getirdi; kullanıcıların kurduğu otomatik
 * sorgular ("Senkron Planları", gece senkronu) canlıya KAPALI çıkar. Anahtar
 * SUNUCUDADIR (`SORGU_SENKRON_AKTIF`) ve `/api/v1/ben` yanıtında üst düzey
 * `senkron_aktif` alanı olarak gelir; istemci derleme zamanı bayrağı kullanmaz,
 * böylece UYAP'la anlaşma olursa tek sunucu ayarıyla, istemci dağıtımı olmadan
 * geri açılabilir.
 *
 * FAIL-CLOSED: alan YOKSA (eski backend, deploy öncesi açılmış oturum, `/ben`
 * henüz yüklenmedi) ya da `true`dan başka her şeyse KAPALI sayılır. Bu kapı
 * yalnız ekranı ve istemcideki zamanlayıcıları kapatır; sunucu kendi kapısını
 * ayrıca uygular (senkron-plan yazma uçları 409 `senkron_kapali` döner).
 *
 * KAPSAM DIŞI (bilerek açık): haciz önerisi bileşenleri, sorgu kaynak rozetleri,
 * elle başlatılan görevler ve ekrandaki tekil sorgular bu kapıya BAĞLI DEĞİLDİR.
 */

/** Kapalıyken kullanıcının geri gönderildiği yer: e-İcraPro ana sayfası (İşlemler). */
export const SENKRON_KAPALI_YONLENDIRME: string = ProgramPaths.eicrapro;

/** Plan ekranının yolu; kapı kapanınca bu ekrandaki kullanıcı dışarı alınır. */
export const SENKRON_PLAN_YOLU: string = ProgramPaths.eicrapro + "/senkron-plani";

/**
 * Kapı değerinin sunucudan tazelenme sıklıkları. `/ben` ağır bir uçtur (adresler,
 * bildirimler, roller, lisans/paket sorguları ve `tenant_users.updated_at` yazımı) ve
 * kullanıcı `sessionStorage`da durduğu için sayfa yenilemesi normalde HİÇ istek atmaz;
 * kapı bu tasarımı bozmasın diye her yerde alt sınırla tazelenir.
 */
/** E-İcraPro'da sayfa/rota girişinde: en çok yarım saatte bir. */
export const SENKRON_KAPI_TAZELEME_ARALIGI_MS: number = 30 * 60 * 1000;
/** Plan ekranı rotası kapalıyken (kullanıcı adresi bilerek yazdı): en çok 30 sn'de bir. */
export const SENKRON_KAPI_ROTA_ARALIGI_MS: number = 30 * 1000;
/** Zamanlayıcı ateşlemesinde (günde en çok iki kez): 10 dk içinde tazelenmişse yeniden sorulmaz. */
export const SENKRON_KAPI_ATESLEME_ARALIGI_MS: number = 10 * 60 * 1000;

/** Kapı değerinin sunucudan en son ne zaman (epoch ms) alındığı; sayfa yenilemesinde kalıcıdır. */
export const SENKRON_KAPI_ZAMANI_ANAHTARI: string = "senkronKapiZamani";

/** `sessionStorage`daki son tazelenme zamanı; yoksa/bozuksa/erişilemezse 0 (hiç tazelenmedi). */
export function senkronKapiZamaniOku(): number {
    try {
        const deger = Number(sessionStorage.getItem(SENKRON_KAPI_ZAMANI_ANAHTARI));
        return isFinite(deger) && deger > 0 ? deger : 0;
    } catch (e) {
        return 0;
    }
}

/** Kapı değerinin şimdi sunucudan alındığını kaydeder (depo erişilemezse sessizce geçer). */
export function senkronKapiZamaniYaz(zaman: number = Date.now()): void {
    try {
        sessionStorage.setItem(SENKRON_KAPI_ZAMANI_ANAHTARI, String(zaman));
    } catch (e) {
        // Depo yoksa her sayfada yeniden istenir; kapının kendisi etkilenmez.
    }
}

/**
 * `sonZaman` (epoch ms), `simdi`ye göre `aralikMs`ten daha yakın mı? `sonZaman` 0 ise
 * (hiç olmadı) ya da saat geriye alınmışsa (`simdi < sonZaman`) HAYIR: bayat bir
 * damga sonsuza dek "taze" kalıp tazelemeyi engellemesin.
 */
export function senkronAralikIcindeMi(sonZaman: number, simdi: number, aralikMs: number): boolean {
    const gecen = simdi - sonZaman;
    return sonZaman > 0 && gecen >= 0 && gecen < aralikMs;
}

/**
 * Eldeki kapı değeri "yeterince taze" mi? Değer gerçek bir boolean olmalı (alan hiç
 * gelmediyse ya da `null`sa taze sayılmaz: deploy öncesi açılmış oturum bir kez
 * tazelenir) ve son tazelenme `aralikMs`ten yakın olmalı.
 */
export function senkronKapisiTazeMi(senkronAktif: any, sonZaman: number, simdi: number, aralikMs: number): boolean {
    return typeof senkronAktif === "boolean" && senkronAralikIcindeMi(sonZaman, simdi, aralikMs);
}

/**
 * Store'un `senkronAktif` getter'ını okur. Store yoksa ya da getter tanımsızsa
 * KAPALI: cevabı bilmediğimiz bir soruya "açık" demek istemci tarafında UYAP'a
 * istek atılması demek.
 */
export function senkronAcikMi(store: any): boolean {
    return !!store && !!store.getters && store.getters.senkronAktif === true;
}

/**
 * Kapıyı ATEŞLEME ANINDA doğrular (22:45 ön-kontrol, 08:00 sabah raporu): önbellekteki
 * değer kapalıysa istek ATMADAN `false` döner; açıksa değeri `senkronDurumunuTazele` ile
 * (alt sınırlı, sessiz) sunucudan teyit eder ve tazelenmiş değere göre karar verir.
 * Günlerce açık kalan bir sekme, sunucu sonradan yeniden kapatılsa da eski "açık"
 * değerle UYAP'a istek atmaya devam etmesin diye gereklidir.
 *
 * Tazeleme hata verirse ya da store `dispatch` taşımıyorsa (yalın sahte nesne) eldeki
 * değerle karar verilir; eylem zaten kendi içinde hatayı yutar.
 */
export async function senkronAcikMiTazeleyerek(store: any, enAzAralikMs: number): Promise<boolean> {
    if (!senkronAcikMi(store)) return false;

    if (typeof store.dispatch === "function") {
        try {
            await store.dispatch("senkronDurumunuTazele", {enAzAralikMs});
        } catch (e) {
            // Tazeleme başarısızsa eldeki değerle sürülür.
        }
    }
    return senkronAcikMi(store);
}

/**
 * Menü kalemlerini kapıya göre süzer.
 *
 * `senkronKapisi: true` işaretli kalem (üst düzey ya da alt kalem) kapı kapalıyken
 * görünmez. Bir grubun TÜM alt kalemleri kapı yüzünden düşerse grubun kendisi de
 * düşer (boş açılır başlık bırakılmaz). Kapı açıkken girdi aynen döner; girdi
 * dizisi ve kalemleri DEĞİŞTİRİLMEZ (menü tanımı sınıf alanında durur).
 */
export function menuKalemleriniSuz(kalemler: Array<any>, senkronAcik: boolean): Array<any> {
    if (senkronAcik || !Array.isArray(kalemler)) return kalemler;

    const sonuc: Array<any> = [];
    kalemler.forEach((kalem) => {
        if (!kalem || kalem.senkronKapisi === true) return;

        if (Array.isArray(kalem.altbasliklar)) {
            const kalanlar = kalem.altbasliklar.filter((alt: any) => !alt || alt.senkronKapisi !== true);
            if (kalanlar.length === 0 && kalem.altbasliklar.length > 0) return;
            sonuc.push(kalanlar.length === kalem.altbasliklar.length ? kalem : {...kalem, altbasliklar: kalanlar});
            return;
        }

        sonuc.push(kalem);
    });
    return sonuc;
}

/**
 * Plan ekranı rotasının kararı: `null` = geçebilir, metin = o yola yönlendir.
 *
 * Kapalıyken önce sunucudan durumu tazeler: `sessionStorage`daki kullanıcı bayat
 * olabilir ve kapı yeniden açıldığında yer imiyle gelen kullanıcı haksız yere geri
 * çevrilmesin. Tazeleme en çok `SENKRON_KAPI_ROTA_ARALIGI_MS`de bir istektir (eşzamanlı
 * çağrılar aynı isteği paylaşır) ve başarısız olursa mevcut değerle karar verilir.
 */
export async function senkronRotasiYonlendirmesi(store: any): Promise<string | null> {
    if (!senkronAcikMi(store)) {
        try {
            await store.dispatch("senkronDurumunuTazele", {enAzAralikMs: SENKRON_KAPI_ROTA_ARALIGI_MS});
        } catch (e) {
            // Tazeleme hatası kapıyı AÇMAZ: aşağıda mevcut değerle karar verilir.
        }
    }
    return senkronAcikMi(store) ? null : SENKRON_KAPALI_YONLENDIRME;
}

/**
 * Kapı kapandığında (sunucu 409 `senkron_kapali` döndü ya da `/ben` yenilendi) plan
 * ekranında duran kullanıcıyı dışarı alır; kapalı bir özelliğin ekranı açık
 * kalmasın. Yönlendirdiyse `true` döner. Plan ekranında değilse ya da kapı açıksa
 * hiçbir şey yapmaz.
 */
export function senkronEkranindanCikar(aktif: boolean, mevcutYol: string, router: any): boolean {
    // Router sondaki `/`yi de aynı rota sayar (`/eicrapro/senkron-plani/`); `path` olduğu gibi gelir.
    const yol = String(mevcutYol ?? "").replace(/\/+$/, "");
    if (aktif || yol !== SENKRON_PLAN_YOLU || !router) return false;

    router.replace(SENKRON_KAPALI_YONLENDIRME);
    return true;
}
